import { apiClient } from "./client";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import { organizationScopedSessionStorageKey } from "@/lib/organization-scope";

type Schemas = publicOperatorAdmin.components["schemas"];

export type ServiceSubscriptionSummary = Schemas["ServiceSubscriptionSummary"];
export type ServiceSubscriptionTerminationResponse = Schemas["ServiceSubscriptionTerminationResponse"];
export type CheckoutSessionCreateRequest = Schemas["CheckoutSessionCreateRequest"];
export type CheckoutSessionResponse = Schemas["CheckoutSessionResponse"];
export type PlanChangeEstimateRequest = Schemas["PlanChangeEstimateRequest"];
export type PlanChangeEstimateResponse = Schemas["PlanChangeEstimateResponse"];
export type PlanChangeScheduleRequest = Schemas["PlanChangeScheduleRequest"];
export type PlanChangeScheduleResponse = Schemas["PlanChangeScheduleResponse"];
export type BillingConfirmResponse = Schemas["BillingConfirmResponse"];
export type PaidCatalogGrade = Schemas["PaidCatalogGrade"];
export type PaidCatalogResponse = Schemas["PaidCatalogResponse"];
export type BillingPortalSessionResponse = Schemas["BillingPortalSessionResponse"];
export type InvoiceSubscriptionCreateRequest = Schemas["InvoiceSubscriptionCreateRequest"];
export type OpenInvoiceSummary = Schemas["OpenInvoiceSummary"];

export function fetchServiceSubscription(): Promise<ServiceSubscriptionSummary | undefined> {
  return apiClient<ServiceSubscriptionSummary | undefined>("/service-subscription");
}

export function fetchPaidCatalog(): Promise<PaidCatalogResponse> {
  return apiClient<PaidCatalogResponse>("/billing/paid-catalog");
}

export function terminateServiceSubscription(): Promise<ServiceSubscriptionTerminationResponse> {
  return withBillingIdempotency("service-cancel", {}, (idempotencyKey) => apiClient<ServiceSubscriptionTerminationResponse>("/service-subscription", {
    method: "DELETE",
    headers: { "Idempotency-Key": idempotencyKey },
  }));
}

export function withdrawServiceCancellation(): Promise<ServiceSubscriptionSummary> {
  return withBillingIdempotency("service-cancel-withdraw", {}, (idempotencyKey) => apiClient<ServiceSubscriptionSummary>("/service-subscription/scheduled-cancellation", {
    method: "DELETE",
    headers: { "Idempotency-Key": idempotencyKey },
  }));
}

export function createBillingPortalSession(): Promise<BillingPortalSessionResponse> {
  return apiClient<BillingPortalSessionResponse>("/service-subscription/billing-portal-sessions", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function createCheckoutSession(
  body: CheckoutSessionCreateRequest,
): Promise<CheckoutSessionResponse> {
  return withBillingIdempotency("checkout", body, (idempotencyKey) => apiClient<CheckoutSessionResponse>("/service-subscription/checkout-sessions", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  }));
}

/** D8: starts invoice payment, or charges an immediate change of an invoice contract on a new invoice. */
export function createInvoiceSubscription(
  body: InvoiceSubscriptionCreateRequest,
): Promise<BillingConfirmResponse> {
  return withBillingIdempotency("invoice-subscription", body, (idempotencyKey) => apiClient<BillingConfirmResponse>("/service-subscription/invoice-subscriptions", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  }));
}

export function createPlanChangeEstimate(
  body: PlanChangeEstimateRequest,
): Promise<PlanChangeEstimateResponse> {
  return apiClient<PlanChangeEstimateResponse>("/service-subscription/plan-change-estimates", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function createPlanChangeSchedule(
  body: PlanChangeScheduleRequest,
): Promise<PlanChangeScheduleResponse> {
  return withBillingIdempotency("plan-schedule", body, (idempotencyKey) => apiClient<PlanChangeScheduleResponse>("/service-subscription/plan-change-schedule", {
    method: "PUT",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  }));
}

export function cancelPlanChangeSchedule(): Promise<ServiceSubscriptionSummary> {
  return withBillingIdempotency("plan-schedule-cancel", {}, (idempotencyKey) => apiClient<ServiceSubscriptionSummary>("/service-subscription/plan-change-schedule", {
    method: "DELETE",
    headers: { "Idempotency-Key": idempotencyKey },
  }));
}

export function confirmBilling(cancelCheckout = false): Promise<BillingConfirmResponse> {
  return withBillingIdempotency("billing-confirm", { cancelCheckout }, (idempotencyKey) => apiClient<BillingConfirmResponse>("/service-subscription/billing-confirms", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ cancelCheckout }),
  }));
}

async function withBillingIdempotency<T>(
  family: string,
  payload: unknown,
  request: (idempotencyKey: string) => Promise<T>,
) {
  const cacheKey = organizationScopedSessionStorageKey(
    `kdrive.billing-idempotency:${family}:${JSON.stringify(payload)}`,
  );
  const existing = cacheKey ? sessionStorage.getItem(cacheKey) : null;
  const idempotencyKey = existing ?? crypto.randomUUID();
  if (cacheKey && !existing) sessionStorage.setItem(cacheKey, idempotencyKey);
  const result = await request(idempotencyKey);
  if (cacheKey) sessionStorage.removeItem(cacheKey);
  return result;
}
